import type { Topic } from "@app/core";

/** Horizontal drag distance (px) a topic row must travel before the Organize
 *  panel treats the gesture as indent/outdent rather than a plain vertical
 *  reorder — see `TopicsPanel.tsx`. */
export const REPARENT_DRAG_THRESHOLD = 40;

export interface FlatTopic {
  topic: Topic;
  depth: number;
}

/**
 * Order topics as a depth-first tree: every parent is immediately followed by
 * its descendants. Siblings are sorted by the user's `order` (ids in their
 * preferred sequence; unknown ids fall back to name order, stable). A topic
 * whose parent is missing or deleted is treated as a root so it never disappears.
 */
export function flattenTopics(topics: Topic[], order: string[] = []): FlatTopic[] {
  const ids = new Set(topics.map((t) => t.id));
  const rank = new Map(order.map((id, i) => [id, i] as const));
  const byParent = new Map<string | null, Topic[]>();
  for (const t of topics) {
    const key = t.parent_id && ids.has(t.parent_id) ? t.parent_id : null;
    const list = byParent.get(key) ?? [];
    list.push(t);
    byParent.set(key, list);
  }
  for (const list of byParent.values()) {
    list.sort((a, b) => {
      const ra = rank.get(a.id);
      const rb = rank.get(b.id);
      if (ra !== undefined && rb !== undefined) return ra - rb;
      if (ra !== undefined) return -1;
      if (rb !== undefined) return 1;
      return a.name.localeCompare(b.name);
    });
  }

  const out: FlatTopic[] = [];
  const walk = (parent: string | null, depth: number) => {
    for (const t of byParent.get(parent) ?? []) {
      out.push({ topic: t, depth });
      walk(t.id, depth + 1);
    }
  };
  walk(null, 0);
  return out;
}

/**
 * `id` plus all of its descendants. Used to forbid cyclic re-parenting (a topic
 * may not become a child of itself or one of its own descendants).
 */
export function descendantIds(topics: Topic[], id: string): Set<string> {
  const childrenOf = new Map<string, Topic[]>();
  for (const t of topics) {
    if (!t.parent_id) continue;
    const list = childrenOf.get(t.parent_id) ?? [];
    list.push(t);
    childrenOf.set(t.parent_id, list);
  }
  const out = new Set<string>([id]);
  const stack = [id];
  while (stack.length) {
    const cur = stack.pop()!;
    for (const c of childrenOf.get(cur) ?? []) {
      if (!out.has(c.id)) { out.add(c.id); stack.push(c.id); }
    }
  }
  return out;
}
